import type { paths } from "@/openapi.gen.ts";
import { m } from "@/paraglide/messages.js";
import type { Middleware } from "openapi-fetch";
import createFetchClient from "openapi-fetch";
import createClient from "openapi-react-query";
import { toast } from "@/utils/toast.ts";
import { forgetAccountByToken } from "./remembered-accounts.ts";
import { traceMiddleware } from "./telemetry.ts";

// Debounce window for parallel 401s (TanStack retry + concurrent queries).
const LOGOUT_DEBOUNCE_MS = 5000;
let lastLogoutAt: number | null = null;

// Keep token in module state (safe for client-side apps)
// This allows middleware to access the most up-to-date token
let accessToken: string | null = null;

// Store logout callback for middleware
let logoutCallback: (() => void) | null = null;
let navigationCallback: ((path: string) => void) | null = null;

/**
 * Auth middleware that keeps client alive
 * - Updates token dynamically from module state
 * - Handles 401 error
 * - Adds dev logging
 * Follows: https://openapi-ts.dev/openapi-fetch/middleware-auth
 */
const authMiddleware: Middleware = {
	async onRequest({ request }) {
		// Add Authorization header if token exists
		if (accessToken) {
			request.headers.set("authorization", `Bearer ${accessToken}`);
		}

		// Dev logging
		if (import.meta.env.DEV && request.url.includes("/api/auth")) {
			console.debug("[Auth] Request:", {
				method: request.method,
				url: new URL(request.url).pathname,
				hasAuth: request.headers.has("authorization"),
			});
		}

		return request;
	},

	async onResponse({ response }) {
		// Public endpoints (e.g. a shared artifact behind a project/private
		// access level) return 401 to mean "sign in to view this", which the
		// route handles itself — not an expired session, so skip the logout flow.
		const isPublicEndpoint = new URL(response.url).pathname.startsWith(
			"/api/public/",
		);
		if (response.status === 401 && !isPublicEndpoint) {
			handleSessionExpired();
			if (import.meta.env.DEV) {
				console.warn(
					`[Auth] Session expired (${response.status}). Redirecting to login.`,
				);
			}
		}

		// Dev logging
		if (import.meta.env.DEV && response.url.includes("/api/auth")) {
			console.debug("[Auth] Response:", {
				status: response.status,
				url: new URL(response.url).pathname,
			});
		}

		return response;
	},
};

// Create client once and reuse it
const fetchClient = createFetchClient<paths>();
fetchClient.use(traceMiddleware);
fetchClient.use(authMiddleware);

export const $api = createClient(fetchClient);

// ✅ Export for services that need direct fetch access (e.g., loginUser, getChallenge)
export { fetchClient };

/**
 * Trigger the session-expired flow: clear token, toast, and redirect to login.
 * Shared between the 401 middleware and other code paths that detect an
 * expired session out-of-band (e.g. chat WebSocket auth_session_expired event).
 * Debounced so parallel triggers don't stack toasts or navigations.
 */
export function handleSessionExpired(): void {
	const now = Date.now();
	if (lastLogoutAt !== null && now - lastLogoutAt < LOGOUT_DEBOUNCE_MS) {
		return;
	}
	lastLogoutAt = now;

	// The token is dead (expired, revoked, the member deactivated), so the
	// organization switcher must not offer it again. Every other remembered
	// account stays: their sessions are independent of this one.
	if (accessToken !== null) {
		forgetAccountByToken(accessToken);
	}
	accessToken = null;
	localStorage.removeItem("authToken");

	toast.error(m.auth_session_expired());

	if (logoutCallback) {
		logoutCallback();
	}

	if (navigationCallback) {
		navigationCallback("/auth/login");
	}
}

/**
 * Whether the session-expired flow fired in the last `withinMs` milliseconds.
 *
 * This exists so the boot path can tell a real expired session apart from a
 * backend outage. It used to infer that from `localStorage.authToken` being
 * gone, which is a weaker signal than it looks: `logout()` removes the token
 * directly without coming through here, so an explicit sign-out raced against a
 * failing restore looked identical to a 401 and sent the user to login while the
 * backend was merely unreachable.
 *
 * Asking this instead is precise, because every caller of the flow means the
 * same thing — the session really is gone. The 401 middleware, the cross-tab
 * storage listener and the hard-expiry monitor are the only three, and for all
 * of them login is the right destination.
 *
 * `handleSessionExpired` already records its own timestamp to debounce parallel
 * 401s, so this reads existing state rather than adding bookkeeping.
 */
export function didSessionExpireRecently(withinMs: number): boolean {
	if (lastLogoutAt === null) {
		return false;
	}
	return Date.now() - lastLogoutAt < withinMs;
}

/**
 * Update token in module state (not recreate client)
 * This is much more efficient and follows OpenAPI TypeScript patterns.
 * Middleware automatically uses the updated token on next request.
 */
export function updateAuthHeaders(token: string | null): void {
	accessToken = token;

	if (import.meta.env.DEV) {
		console.debug("[Auth] Token updated", { hasToken: !!token });
	}
}

/** The token requests are currently sent with — the cross-tab listener compares against it. */
export function getAccessToken(): string | null {
	return accessToken;
}

/**
 * Register callbacks for logout and navigation
 * Called from AuthProvider to enable auth error handling
 */
export function registerAuthCallbacks(
	logout: () => void,
	navigate: (path: string) => void,
): void {
	logoutCallback = logout;
	navigationCallback = navigate;
}
